import { Skeleton } from "@mui/material";
import { styled } from "@mui/styles";
import React from "react";
import api from "src/store/features/api";
import { useUserSlice } from "src/store/features/user.slice";
import { useWatchlist } from "src/hooks/useWatchlist";
import { useAppSelector } from "src/store/store";
import IconMapping from "src/utils/iconMapping";
import { IWatchlistData, TRENDING_API_PARAMS } from "./defaults";
import { formatChange, formatPrice, formatWatchlistData } from "./helpers";
import PortfolioTable from "./watchlist/PortfolioTable";
import WatchlistAndTrendingTable from "./watchlist/WatchlistAndTrendingTable";
import WatchlistTabs from "./watchlist/WatchlistTabs";
import YourPortfolioDefault from "./watchlist/YourPortfolioDefault";
import { trackEvent } from "src/Amplitude";

export const GreenPlus = styled("span")({
  color: "#056645",
  backgroundColor: "#F7FFFC",
});

export const RedMinus = styled("span")({
  color: "#790202",
  backgroundColor: "#FFFBFB",
});

const YourCrypto = () => {
  const userSlice = useUserSlice();
  const {
    defaultCurrency: { code },
    isUnverified,
  } = userSlice;

  // Use watchlist hook for managing watchlist operations
  // The hook reads from Redux, which is already populated by Header component on mount
  const { toggleWatchlist, isCoinInWatchlist } = useWatchlist({
    trackingContext: "Portfolio",
  });

  // Directly subscribe to Redux watchlist to ensure component re-renders on changes
  // This ensures the component always has the latest watchlist state
  const watchlist = useAppSelector((state) => state.user.watchlist);

  // call api here
  // get portfolio holdings data.
  const { data: portfolio, isLoading: portfolioIsLoading } = api.useGetWalletPageHoldingsNewQuery(
    { baseCurrency: code },
    { skip: !code },
  );
  // get trending tokens- at least top 5.
  const { data: trending } = api.useGetTrendingCryptoCurrenciesQuery(
    {
      ...TRENDING_API_PARAMS,
      quote: code,
    },
    { skip: !code },
  );

  // on filter click
  const [selectedfilter, setSelectedFilter] = React.useState<string>("portfolio");
  const handleOnFilterClick = (opt: string): void => {
    trackEvent(`Button_Click`, {
      button: `Watchlist_${opt}`,
    });
    setSelectedFilter(opt);
  };

  // Format Redux watchlist to IWatchlistData format
  // Pass isCoinInWatchlist to ensure favourite status is accurate
  const watchlistState = React.useMemo((): IWatchlistData[] => {
    if (!watchlist || !code) return [];
    return formatWatchlistData(watchlist, code, isCoinInWatchlist);
  }, [watchlist, code, isCoinInWatchlist]);

  // React hook to memoize and format data for the Watchlist component
  const data = React.useMemo((): IWatchlistData[] => {
    // Early return if essential data is missing
    if (!portfolio || !watchlistState || !trending || !code) return [];

    // Helper function to format portfolio data
    const formatPortfolioData = () => {
      const sortedPortfolio = [...portfolio].sort((a, b) => b.totalValue - a.totalValue);

      return sortedPortfolio.map((asset) => {
        // Check Redux directly for accurate favorite status (case-insensitive)
        const isFavourite = isCoinInWatchlist(asset.currencyConfig.code);
        const currencyCode = asset.currencyConfig.code;

        const formattedChange = formatChangeComponent(asset.change);

        return {
          name: asset.currencyConfig.displayName ?? "",
          code: currencyCode,
          currencyConfig: asset.currencyConfig,
          icon: asset.currencyConfig.logoUrl ?? IconMapping(currencyCode.toLowerCase(), 24),
          rawPrice: asset.price,
          price: formatPrice(asset.price, code, 0),
          units: `${asset.total.toFixed(2)}`,
          sparkline: asset.sparkline
            ? asset.sparkline.split(",").filter((item) => item.trim() !== "")
            : [],
          change: formattedChange,
          marketCap: formatPrice(asset.totalValue, code, 0),
          favourite: isFavourite,
          alert: true,
        };
      });
    };

    // Helper function to format change percentage with color coding
    const formatChangeComponent = (change: number | undefined) => {
      if (!change) return <span translate="no">{formatChange(0)}</span>;

      if (change > 0) {
        return (
          <GreenPlus data-testid="#yourcrypto-positive-percent-change" translate="no">
            +{formatChange(change).replace(/^[-+]/, "")}
          </GreenPlus>
        );
      }
      if (change < 0) {
        return (
          <RedMinus data-testid="#yourcrypto-negative-percent-change" translate="no">
            -{formatChange(change).replace(/^[-+]/, "")}
          </RedMinus>
        );
      }
      return <span translate="no">{formatChange(change ?? 0)}</span>;
    };

    // Helper function to format trending data
    const formatTrendingData = () => {
      // Pass isCoinInWatchlist to formatWatchlistData so favourite status is set correctly
      return formatWatchlistData(trending, code, isCoinInWatchlist);
    };

    // Select and format data based on the selected filter
    if (selectedfilter === "portfolio") return formatPortfolioData();
    if (selectedfilter === "trending") return formatTrendingData();

    // Default case: return watchlist data if no specific filter is selected
    return watchlistState;
  }, [portfolio, trending, code, selectedfilter, watchlistState, isCoinInWatchlist, watchlist]);

  React.useEffect(() => {
    // Check if essential data is available
    if (!watchlist || !code || !portfolio) return;
    if ((watchlist.length <= 0 && portfolio.length <= 0) || isUnverified) {
      setSelectedFilter("trending");
    }
  }, [watchlist, code, portfolio, isUnverified]);

  const addOrRemove = (coin: IWatchlistData) => {
    // Use the hook to toggle watchlist status
    toggleWatchlist(coin.code, coin.currencyConfig);
  };

  // SHOW_LOADING: Determines when to display loading state
  // Returns true if any of these conditions are met: Portfolio is loading AND no data exists yet, No default currency code exists
  const SHOW_LOADING = (portfolioIsLoading && data.length <= 0) || !code;

  // SHOW_DEFAULT: Determines when to show default/empty state
  // Returns true if: User is unverified OR no data exists AND current filter is set to "portfolio"
  const SHOW_DEFAULT = (isUnverified || data.length <= 0) && selectedfilter === "portfolio";
  if (SHOW_LOADING) return <Skeleton variant="rounded" width={"100%"} height={"302px"} />;

  return (
    <>
      <WatchlistTabs selectedfilter={selectedfilter} onFilterClick={handleOnFilterClick} />
      {SHOW_DEFAULT ? (
        <YourPortfolioDefault selectedfilter={selectedfilter} isUnverified={isUnverified} />
      ) : (
        <>
          {selectedfilter === "portfolio" && (
            <PortfolioTable
              data={data}
              selectedfilter={selectedfilter}
              onFavouriteClick={addOrRemove}
            />
          )}
          {(selectedfilter === "watchlist" || selectedfilter === "trending") && (
            <WatchlistAndTrendingTable
              data={data}
              selectedfilter={selectedfilter}
              onFavouriteClick={addOrRemove}
            />
          )}
        </>
      )}
    </>
  );
};

export default YourCrypto;
